import { Injectable, NgZone } from '@angular/core';
import gql from 'graphql-tag';
import { Observable, timer } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';
import { AppSyncService } from 'src/app/core/appsync.service';
import { environment } from 'src/environments/environment';
import { extractFindings, getAiScribeResult, getAiScribeUsage } from 'src/graphql/queries';
import {
  AIScribeUsageData,
  AssessmentAIScribeInput,
  AssessmentAIScribeResult
} from './assessment-ai-scribe.model';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 120; // 6 min max

@Injectable()
export class AssessmentAIScribeService {
  constructor(private appSyncService: AppSyncService, private ngZone: NgZone) {}

  extract(input: AssessmentAIScribeInput): Observable<AssessmentAIScribeResult> {
    const baseInput: Record<string, unknown> = {
      assessmentId: input.assessmentId,
      ...(input.mode ? { mode: input.mode } : {}),
      ...(input.assessmentMethod ? { assessmentMethod: input.assessmentMethod } : {}),
      ...(input.enabledSections && input.enabledSections.length > 0
        ? { enabledSections: input.enabledSections }
        : {})
    };

    const variables = {
      input: input.s3Key
        ? {
            ...baseInput,
            s3Object: {
              key: input.s3Key,
              bucket: input.bucketName,
              region: environment.userUploads.region
            }
          }
        : {
            ...baseInput,
            textInput: input.transcript
          }
    };

    return this.appSyncService.hydrated().pipe(
      switchMap(
        client =>
          new Observable<AssessmentAIScribeResult>(observer => {
            // Step 1: kick off job, get jobId back
            this.query(client, extractFindings, variables)
              .then(jobIdRaw => {
                const raw = typeof jobIdRaw === 'string' ? jobIdRaw : JSON.stringify(jobIdRaw);

                // Old sync Lambda returns the result directly — detect by presence of 'findings'
                if (raw.includes('"findings"')) {
                  const result = typeof jobIdRaw === 'string' ? JSON.parse(jobIdRaw) : jobIdRaw;
                  this.ngZone.run(() => {
                    observer.next(result);
                    observer.complete();
                  });
                  return;
                }

                // New async Lambda returns a jobId UUID — poll for result
                const jobId = raw.replace(/"/g, '');
                let polls = 0;
                const sub = timer(0, POLL_INTERVAL_MS)
                  .pipe(takeWhile(() => polls++ < MAX_POLLS))
                  .subscribe(async () => {
                    try {
                      const pollRaw = await this.query(client, getAiScribeResult, { id: jobId });
                      const job = typeof pollRaw === 'string' ? JSON.parse(pollRaw) : pollRaw;

                      if (job.status === 'done') {
                        sub.unsubscribe();
                        let result = job.result;
                        if (typeof result === 'string') result = JSON.parse(result);
                        if (typeof result === 'string') result = JSON.parse(result);
                        this.ngZone.run(() => {
                          observer.next(result);
                          observer.complete();
                        });
                      } else if (job.status === 'error') {
                        sub.unsubscribe();
                        this.ngZone.run(() => observer.error(new Error(job.error)));
                      }
                    } catch (err) {
                      sub.unsubscribe();
                      this.ngZone.run(() => observer.error(err));
                    }
                  });

                // After all polls exhausted
                setTimeout(() => {
                  if (!observer.closed) {
                    sub.unsubscribe();
                    this.ngZone.run(() =>
                      observer.error(new Error('Extraction timed out. Please try again.'))
                    );
                  }
                }, POLL_INTERVAL_MS * MAX_POLLS);
              })
              .catch(err => this.ngZone.run(() => observer.error(err)));
          })
      )
    );
  }

  fetchUsage(clinicId: string): Observable<AIScribeUsageData> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => this.query(client, getAiScribeUsage, { clinicId })),
      map(raw => (typeof raw === 'string' ? JSON.parse(raw) : raw))
    );
  }

  private query(client: any, queryStr: string, variables: any): Promise<any> {
    return client
      .query({
        query: gql(queryStr),
        variables,
        fetchPolicy: 'no-cache'
      })
      .then((res: any) => {
        const key = Object.keys(res.data)[0];
        return res.data[key];
      });
  }
}
